import { Component, OnInit } from '@angular/core';
import { Subscription, Observable, from, of, EMPTY } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { BaseComponent } from '../../base/base.component';

import { WizardService } from '../../../services/wizard/wizard.service';
import { EventService } from '../../../services/event/event.service';
import { WizardContactService } from '../../../services/wizard-contact/wizard-contact.service';
import { ErrorService } from '../../../services/error/error.service';

import { IContactSchema, IWizardConfig, IWizardUser } from 'common-lib';
import { IWizardContact } from '../../../models';

import * as _ from 'lodash';
declare var $: any;
@Component({
    selector: 'wizard-selection',
    templateUrl: './selection.component.html',
    styleUrls: ['./selection.component.scss', '../../form-styles.scss']
})
export class SelectionComponent extends BaseComponent implements OnInit {
    public isAddingContact = false;
    public numberOfSelections = 0;
    public vipContacts: IWizardContact[] = [];
    public selection$: Observable<any>;
    public hideVIP = false;
    public toggleAllVIP = false;
    public isRemovingOnly = false;
    public currentUser?: IWizardUser;
    public errorContacts = [];
    private existingContacts: IWizardContact[] = [];
    private selectionSubscription: Subscription;

    constructor(
        public wizardService: WizardService,
        public eventService: EventService,
        private errorService: ErrorService,
        private wizardContactService: WizardContactService
    ) {
        super();
    }

    ngOnInit() {
        this.vipContacts = this.wizardService.vipContacts;
        this.selection$ = this.wizardService.selection$;
        this.currentUser = this.wizardService.currentUser;
        this.subscribeToSelections();

        // TODO-PLUGIN: refactor if logic
        if (this.wizardService.selection.length && this.wizardService.existingContacts?.length)
            this.initExistingContacts(this.wizardService.selection);

        const isScheduledTask = this.wizardService.config.isScheduled;
        if (isScheduledTask) this.hideVIP = true;

        if (this.data.mode === 'class-change-attendees') this.isRemovingOnly = true;

        const wizardConfig = this.wizardService.config;
        if (wizardConfig.contacts) this.addContactsFromQueryParams(wizardConfig.contacts);
    }

    ngOnDestroy() {
        this.selectionSubscription.unsubscribe();
    }

    private subscribeToSelections() {
        this.selectionSubscription = this.wizardService.selection$.subscribe((selections) => {
            this.handleSelectionChange(selections);
        });
    }

    /**
     * 1. Add a contact to invitees list if they exist, otherwise
     * 2. Create a new contact and then add it to the invitees list
     */
    private addContactsFromQueryParams(contacts: IWizardConfig['contacts']) {
        const contactsStream = Array.isArray(contacts) ? contacts : [contacts];
        from(contactsStream)
            .pipe(
                mergeMap((contact) => {
                    // This is a disposable stream
                    // Errors can safely occur in here without killing the original stream

                    // if a contact already exists, it will simply be returned with a new default email set
                    return this.wizardContactService
                        .addNewContact({
                            name: contact.name,
                            email: contact.email
                        })
                        .pipe(
                            catchError((err) => {
                                console.error('Caught Error: ', err);
                                this.errorContacts.push(contact);
                                //Return an empty Observable which gets collapsed in the output
                                return EMPTY;
                            })
                        );
                })
            )
            .subscribe(
                (contact) => {
                    const isVIP = contactsStream.find(
                        (originalContact) => originalContact.email === contact.defaultEmail
                    )?.isVIP;
                    const addedContact = this.wizardService.updateSelection(contact, { shouldDeleteSubject: false });

                    if (isVIP) this.addVIPContact(addedContact);
                },
                (error) => {
                    console.error('Adding contacts failed: ', error);
                }
            );
    }

    /** @params selections: Theoretically, should be able to handle any data but has only been IWizardContact so far */
    private handleSelectionChange(selections: IWizardContact[]) {
        const mode = this.data.mode;

        /**
         * Disable proceeding to the next step when:
         * 1) There are no contacts selected (except for class-remove-attendees, which can have 0 attendees)
         * 2) The current selection is the same as the original selection ie no change
         */
        if (selections.length < 1 && mode !== 'class-change-attendees') {
            this.eventService.isNextDisabled = true;
        } else if (
            (mode === 'change-attendees' || mode === 'class-change-attendees') &&
            this.checkIfContactsAreSame(selections, this.existingContacts)
        ) {
            this.eventService.isNextDisabled = true;
        } else {
            this.handleUpdateNumberOfSelections(selections);
        }
    }

    private initExistingContacts(contacts: IWizardContact[]) {
        this.existingContacts = this.wizardService.existingContacts.length
            ? this.wizardService.existingContacts
            : contacts || [];
        this.wizardService.existingContacts = this.existingContacts;

        // TEMP: Disabled, needs update that only checks this statement if Wizard is using the change-attendees skill AND this is the initial view
        if (this.checkIfContactsAreSame(contacts, this.existingContacts)) this.eventService.isNextDisabled = true;
    }

    private checkIfContactsAreSame(currentContacts: IWizardContact[], existingContacts: IWizardContact[]) {
        return _.xor(_.map(currentContacts, 'id'), _.map(existingContacts, 'id')).length === 0;
    }

    public handleRemove(id: string) {
        const shouldDeleteSubject = this.data.mode !== 'class-change-attendees';

        this.wizardService.removeSelection(id, shouldDeleteSubject);

        const vipContact = this.isVipContactSelected(id, this.vipContacts);
        if (vipContact) {
            this.removeVIPContact(vipContact);
        }
    }

    public handleToggleAllVIP(shouldMarkAllVIP: boolean) {
        if (shouldMarkAllVIP) {
            const allContacts = [...this.wizardService.options.contacts];
            this.vipContacts = this.wizardService.vipContacts = allContacts;
        } else {
            this.vipContacts = this.wizardService.vipContacts = [];
        }
    }

    public handleMarkAsVIP(data: { id: string }) {
        const contacts = this.wizardService.options.contacts || [];
        const selectedContact = _.find(contacts, { id: data.id });
        const vipContacts = this.wizardService.vipContacts;

        /** if currently selected, de-select it */
        if (this.isVipContactSelected(data.id, vipContacts)) {
            this.removeVIPContact(selectedContact);
        } else {
            this.addVIPContact(selectedContact);
        }
    }

    private isVipContactSelected(id: string, selectedVipContacts: IWizardContact[] | null) {
        return selectedVipContacts.find((contact) => {
            return contact.id === id;
        });
    }

    private addVIPContact(contact: IWizardContact) {
        const updatedVIPContacts = this.wizardService.vipContacts.concat([contact]);
        this.vipContacts = this.wizardService.vipContacts = updatedVIPContacts;
    }

    private removeVIPContact(contact: IWizardContact) {
        const updatedVIPContacts = this.wizardService.vipContacts.filter((vipContact) => {
            return vipContact.id !== contact.id;
        });
        this.vipContacts = this.wizardService.vipContacts = updatedVIPContacts;
    }

    public handleCloseAddContact() {
        this.eventService.isAddingContact = false;

        // if other contacts were added, renable the next action
        const shouldStayDisabled = this.wizardService.selection.length < 1;
        this.eventService.clearErrorMessage('add-contact', { shouldStayDisabled });
    }

    /** contactInfo.contact contains the newly updated defaultEmail already */
    public handleUpdateContactEmailDefault(contactInfo: { contact: IContactSchema }) {
        const { contact } = contactInfo;
        //Due to the way the backend was coded, updating a default email involves moving the email to the start of the array
        //Set the first email as defaultEmail
        const updatedEmailArr = _.sortBy(contact.email, (_email) => {
            return _email === contact.defaultEmail ? false : true;
        });
        const updatedContact = {
            name: contact.name,
            title: contact.title,
            organization: contact.organization,
            email: updatedEmailArr,
            defaultEmail: contact.defaultEmail,
            telephone: contact.telephone,
            userId: contact.userId,
            providerId: contact.providerId
        };

        this.eventService.updateContact(updatedContact, contact.id).subscribe(
            (response: IContactSchema) => {
                this.wizardService.transformSelection(response.id, {
                    email: response.email,
                    defaultEmail: response.defaultEmail
                });
                $('body').toast({
                    class: 'success',
                    message: `Updated ${updatedContact.name}'s default email to ${updatedContact.defaultEmail}`
                });
            },
            (error) => {
                this.errorService.setServerError(error);
            }
        );
    }

    public handleAddContact(contact: { name: string; email: string }) {
        this.wizardContactService.addNewContact(contact).subscribe(
            (response) => {
                this.addNewContactToSelection(response);
                this.isAddingContact = this.eventService.isNextDisabled = false;
            },
            (error) => {
                this.isAddingContact = false;
                this.errorService.setServerError(error, 'add-contact');
            },
            () => {
                this.eventService.clearErrorMessage('add-contact');
            }
        );
    }

    private addNewContactToSelection(contact: IContactSchema) {
        $('body').toast({
            class: 'success',
            message: `${contact.name} has been added as a contact!`
        });
        this.isAddingContact = false;
        this.handleCloseAddContact();
        this.wizardService.updateSelection(contact, { shouldDeleteSubject: true });
    }

    private handleUpdateNumberOfSelections(selections: any[]) {
        this.eventService.isNextDisabled = false;
        this.numberOfSelections = selections.length;
    }

    public trackSelection(_index, item: IWizardContact) {
        return item.id;
    }
}
